import React, { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  FormGroup,
  AccordionSummary,
  AccordionDetails,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { DualRangeInput } from "../../common/components/dual-range-input-slider/DualRangeInputSlider";
import { ArrowDropUpOutlined, Check } from "@mui/icons-material";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import Premium from "../../assets/icons/premium.svg";
import { useFilterLogic } from "../../common/hooks/filters";
import { useDebounce } from "../../common/hooks/useDebounce";
import "./filters.scss";

export default function Filters({
  allServices,
  allCategories,
  initialFilters,
  onChange,
  sortSource,
  setSortSource,
}: any) {
  // STATES
  const { filters, setFilters, resetFilters } = useFilterLogic(
    initialFilters,
    onChange
  );

  const [localPriceRange, setLocalPriceRange] = useState<(string | number)[]>([
    initialFilters.priceRange?.[0] ?? "",
    initialFilters.priceRange?.[1] ?? "",
  ]);
  const [localDistanceRange, setLocalDistanceRange] = useState<
    (string | number)[]
  >([
    initialFilters.distanceRange?.[0] ?? "",
    initialFilters.distanceRange?.[1] ?? "",
  ]);

  // HOOKS
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const debouncedPriceRange = useDebounce(localPriceRange, 1000);
  const debouncedDistanceRange = useDebounce(localDistanceRange, 1000);

  const clampRange = (
    min: number,
    max: number,
    limit = 7000
  ): [number, number] => {
    if (min < 0) min = 0;
    if (max > limit) max = limit;

    if (min >= max) {
      return [max, min];
    }

    return [min, max];
  };

  useEffect(() => {
    const min =
      debouncedPriceRange[0] === ""
        ? undefined
        : Number(debouncedPriceRange[0]);
    const max =
      debouncedPriceRange[1] === ""
        ? undefined
        : Number(debouncedPriceRange[1]);

    if (min === undefined && max === undefined) {
      if (filters.priceRange === undefined) {
        return;
      }

      setFilters((prev) => ({ ...prev, priceRange: undefined }));
      return;
    }

    const newMin = min === undefined ? 0 : min;
    const newMax = max === undefined ? 7000 : max;

    const [fixedMin, fixedMax] = clampRange(newMin, newMax);

    if (
      filters.priceRange?.[0] !== fixedMin ||
      filters.priceRange?.[1] !== fixedMax
    ) {
      setFilters((prev) => ({ ...prev, priceRange: [fixedMin, fixedMax] }));
    }
  }, [debouncedPriceRange]);

  useEffect(() => {
    const min =
      debouncedDistanceRange[0] === ""
        ? undefined
        : Number(debouncedDistanceRange[0]);
    const max =
      debouncedDistanceRange[1] === ""
        ? undefined
        : Number(debouncedDistanceRange[1]);

    if (min === undefined && max === undefined) {
      if (filters.distanceRange === undefined) {
        return;
      }

      setFilters((prev) => ({ ...prev, distanceRange: undefined }));
      return;
    }

    const newMin = min === undefined ? 0 : min;
    const newMax = max === undefined ? 150 : max;

    const [fixedMin, fixedMax] = clampRange(newMin, newMax, 150);

    if (
      filters.distanceRange?.[0] !== fixedMin ||
      filters.distanceRange?.[1] !== fixedMax
    ) {
      setFilters((prev) => ({ ...prev, distanceRange: [fixedMin, fixedMax] }));
    }
  }, [debouncedDistanceRange]);

  useEffect(() => {
    setLocalPriceRange([
      filters.priceRange?.[0] ?? "",
      filters.priceRange?.[1] ?? "",
    ]);
    setLocalDistanceRange([
      filters.distanceRange?.[0] ?? "",
      filters.distanceRange?.[1] ?? "",
    ]);
  }, [filters.priceRange, filters.distanceRange]);

  // whenever parent passes new initialFilters
  useEffect(() => {
    setFilters(initialFilters);
  }, [initialFilters]);

  // const filteredServices = useMemo(() => {
  //   if (!filters.needType) {
  //     return allServices;
  //   }
  //   return allServices?.filter((service) =>
  //     filters.needType === "at_need" || filters.needType === "pre_need"
  //       ? service?.needTypes?.includes(filters.needType)
  //       : false
  //   );
  // }, [allServices, filters.needType]);

  return (
    <Box className="filters">
      {/* Filters Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h6">Filters</Typography>
        <Button
          variant="text"
          onClick={() => {
            resetFilters();
          }}
          sx={{
            color: "#182D40",
            textTransform: "capitalize",
            fontWeight: 400,
            fontSize: 16,
          }}
        >
          Reset
        </Button>
      </Box>

      {/* Need Type */}
      <Accordion defaultExpanded className="accordion-component">
        <AccordionSummary
          expandIcon={<ArrowDropUpOutlined style={{ color: "#000" }} />}
          sx={{
            minHeight: "auto",
            "& .MuiAccordionSummary-content": { m: 0, mt: 1, mb: 1 },
            "& .MuiAccordionSummary-content.Mui-expanded": {
              m: 0,
              mt: 1,
              mb: 1,
            },
            borderBottom: "1px solid #e2e8f0",
          }}
        >
          <Typography className="filter-title">Need Type</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          <Box
            display="flex"
            flexDirection="column"
            sx={{
              "& > div": {
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "4px 0",
              },
            }}
          >
            {[
              { value: "at_need", label: "Immediate Need" },
              { value: "pre_need", label: "Preneed" },
            ].map((opt) => (
              <Box
                key={opt.value}
                onClick={() =>
                  setFilters((prev) => ({
                    ...prev,
                    needType: opt.value as "at_need" | "pre_need",
                  }))
                }
              >
                <Typography sx={{ fontSize: 16 }}>{opt.label}</Typography>
                {filters.needType === opt.value && (
                  <Check
                    sx={{
                      color: "#182D40",
                      fontSize: 18,
                    }}
                  />
                )}
              </Box>
            ))}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Services Accordion */}
      <Accordion defaultExpanded className="accordion-component">
        <AccordionSummary
          expandIcon={
            <ArrowDropUpOutlined
              style={{
                color: "#000",
              }}
            />
          }
          sx={{
            minHeight: "auto",
            "& .MuiAccordionSummary-content": { m: 0, mt: 1, mb: 1 },
            "& .MuiAccordionSummary-content.Mui-expanded": {
              m: 0,
              mt: 1,
              mb: 1,
            },
            borderBottom: "1px solid #e2e8f0",
          }}
        >
          <Typography className="filter-title">Service Categories</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <FormGroup>
            {allCategories?.map((category: any) => (
              <FormControlLabel
                key={category.id}
                control={
                  <Checkbox
                    name={category.id.toString()}
                    checked={filters.services[category.id] || false}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        services: {
                          ...prev.services,
                          [category.id]: e.target.checked,
                        },
                      }))
                    }
                    sx={{
                      color: "#8f8f8f",
                      "&.Mui-checked": {
                        color: "#e9ae10",
                      },
                    }}
                  />
                }
                label={category.name}
              />
            ))}
          </FormGroup>
        </AccordionDetails>
      </Accordion>

      {/* Price */}
      <Accordion defaultExpanded className="accordion-component">
        <AccordionSummary
          expandIcon={
            <ArrowDropUpOutlined
              style={{
                color: "#000",
              }}
            />
          }
          sx={{
            minHeight: "auto",
            "& .MuiAccordionSummary-content": { m: 0, mt: 1, mb: 1 },
            "& .MuiAccordionSummary-content.Mui-expanded": {
              m: 0,
              mt: 1,
              mb: 1,
            },
            borderBottom: "1px solid #e2e8f0",
          }}
        >
          <Typography className="filter-title">Price</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box
            display="flex"
            gap={2}
            mb={1}
            mt={1}
            sx={{
              flexDirection: isMobile ? "column" : "row",
              gap: isMobile ? 1 : 2,
            }}
          >
            <DualRangeInput
              label=""
              minLimit={0}
              maxLimit={7000}
              value={filters.priceRange}
              onChange={(range) => {
                setFilters((prev) => ({ ...prev, priceRange: range }));
              }}
              unit="$"
              step={100}
            />
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Distance Filter */}
      <Accordion defaultExpanded className="accordion-component">
        <AccordionSummary
          expandIcon={
            <ArrowDropUpOutlined
              style={{
                color: "#000",
              }}
            />
          }
          sx={{
            minHeight: "auto",
            "& .MuiAccordionSummary-content": { m: 0, mt: 1, mb: 1 },
            "& .MuiAccordionSummary-content.Mui-expanded": {
              m: 0,
              mt: 1,
              mb: 1,
            },
            borderBottom: "1px solid #e2e8f0",
          }}
        >
          <Typography className="filter-title">Distance</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {/* Distance Inputs */}
          <Box
            display="flex"
            gap={2}
            mb={1}
            mt={1}
            sx={{
              flexDirection: isMobile ? "column" : "row",
              gap: isMobile ? 1 : 2,
            }}
          >
            <DualRangeInput
              label=""
              minLimit={0}
              maxLimit={150}
              value={filters.distanceRange}
              onChange={(range) => {
                setFilters((prev) => ({ ...prev, distanceRange: range }));
              }}
              unit="mi"
              step={5}
            />
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Star Rating Accordion */}
      <Accordion defaultExpanded className="accordion-component">
        <AccordionSummary
          expandIcon={
            <ArrowDropUpOutlined
              style={{
                color: "#000",
              }}
            />
          }
          sx={{
            minHeight: "auto",
            "& .MuiAccordionSummary-content": { m: 0, mt: 1, mb: 1 },
            "& .MuiAccordionSummary-content.Mui-expanded": {
              m: 0,
              mt: 1,
              mb: 1,
            },
            borderBottom: "1px solid #e2e8f0",
          }}
        >
          <Typography className="filter-title">Star Rating</Typography>
        </AccordionSummary>

        <AccordionDetails>
          {/** Will enable in future */}
          {/* <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "60%",
              gap: 1,
              mt: 1,
            }}
          >
            <Button
              sx={{
                border: "1px solid #BFC0C2",
                color:
                  filters.sortBy === "rating_high_to_low" &&
                  sortSource === "filters"
                    ? "#fff"
                    : "#4B4F50",
                bgcolor:
                  filters.sortBy === "rating_high_to_low" &&
                  sortSource === "filters"
                    ? "#ebd28d"
                    : "transparent",
                textTransform: "capitalize",
              }}
              endIcon={
                <StarRoundedIcon
                  sx={{
                    color:
                      filters.sortBy === "rating_high_to_low" &&
                      sortSource === "filters"
                        ? "9aa0a8"
                        : "",
                  }}
                />
              }
              onClick={() => {
                setSortSource("filters");
                setFilters((prev) => ({
                  ...prev,
                  sortBy:
                    prev.sortBy === "rating_high_to_low"
                      ? undefined
                      : "rating_high_to_low",
                }));
              }}
            >
              Top Rated
            </Button>

            <Button
              sx={{
                border: "1px solid #BFC0C2",
                color: "#4B4F50",
                textTransform: "capitalize",
              }}
              endIcon={
                <img
                  src={Premium}
                  alt="Premium Icon"
                  style={{
                    width: 20,
                    height: 20,
                    objectFit: "contain",
                  }}
                />
              }
            >
              Premium
            </Button>
          </Box> */}
          <Box
            sx={{
              display: "flex",
              gap: 1,
              marginTop: 2,
            }}
          >
            {[5, 4, 3].map((star) => {
              const isSelected = filters.ratings?.includes(star);

              return (
                <Button
                  key={star}
                  sx={{
                    border: "1px solid #BFC0C2",
                    color: isSelected ? "#fff" : "#4B4F50",
                    bgcolor: isSelected ? "#ebd28d" : "transparent",
                    padding: "5px 0px !important",
                    margin: "0px !important",
                    minWidth: "48px",
                    flex: 1,
                  }}
                  onClick={() =>
                    setFilters((prev) => {
                      const current = prev.ratings || [];
                      return {
                        ...prev,
                        ratings: isSelected
                          ? current.filter((r) => r !== star)
                          : [...current, star],
                      };
                    })
                  }
                  endIcon={
                    <StarRoundedIcon
                      sx={{
                        color: isSelected ? "" : "#9aa0a8",
                      }}
                    />
                  }
                >
                  {star}
                </Button>
              );
            })}
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
